import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useIsColumnHovered } from 'components/spreadsheet/hooks/useHoveredColumn'
import { tv } from 'tailwind-variants'
import Input from 'components/input'
import { Dots, Join, ErrorOutline } from 'components/icons'
import { IColumnTypes } from 'components/spreadsheet/types'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import { useColumnRename } from 'components/spreadsheet/hooks/useColumnRename'
import { hasSyntheticColumns, ViewTypes } from 'components/spreadsheet/constants/const'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { reorderTransposedColumn } from 'components/spreadsheet/helpers/transpose'
import { moveColumnBlock, resolveDraggedColumnBlock } from 'components/spreadsheet/helpers/columnOrder'
import { COLUMN_MIN_WIDTH, HEADER_HEIGHT, HEADER_HEIGHT_CLASS } from 'style/constants'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { EditorContent, IContextMenuState, ITableViewColumn } from 'types'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { getPlainTextDescription } from 'components/spreadsheet/helpers/format'
import IconSelector from '../cell/components/icon'
import Tooltip from 'components/tooltip'
import InfoHint from 'components/info-hint'
import { useThemedColours } from 'hooks/useTheme'
import { rowButtonProps } from 'components/sidebar/rowButton'
import { indicatorsThatFit, indicatorsWidth, measureWords } from './fitIndicators'

const headerStyles = tv({
  base: 'group/header box-border inline-block cursor-grab bg-surface-sunken text-body border-r border-r-divider',
  variants: {
    frozen: {
      true: 'sticky z-[6]'
    },
    lastFrozen: {
      true: 'shadow-[3px_0_1px_var(--color-divider)]',
      false: 'shadow-none'
    },
    dragHighlight: {
      true: 'border-r-pink'
    },
    noBorder: {
      true: 'border-r-0'
    },
    selected: {
      true: 'z-[7]'
    }
  }
})

interface HeaderProps {
  cumulativeWidth?: number
  width?: number
  optionClick?: (menuState: IContextMenuState) => void
  columnNumber?: number
  columnId?: string
  kind?: IColumnTypes
  value?: string
  backgroundColor?: string
  border?: string
  frozen: boolean
  locked?: boolean
  joined?: boolean
  /** What acts on the column (its sort, group, filters…): icons at the header's right, each named on hover. */
  indicators?: HeaderIndicator[]
  dragOver?: string
  setDragOver?: (dragOver: string) => void
  isAdmin?: boolean
  isSynced?: boolean
  isLastFrozenColumn: boolean
  viewId?: string
  draggedColumnId?: string
  setDraggedColumnId?: (draggedColumnId: string) => void
  setQuickFilterMenu?: (menuState: IContextMenuState) => void
  textColor?: string
  description?: EditorContent | null
  position?: 'relative' | 'absolute'
}

export interface HeaderIndicator {
  key: string
  icon: React.ReactNode
  /** What it means, for its tooltip and a screen reader: "Sorted A to Z". */
  label: string
  /** Drawn in place of the icon and its tooltip, for a mark with a card and an action of its own. */
  element?: React.ReactNode
}

const Header: React.FC<HeaderProps> = ({
  cumulativeWidth,
  width,
  optionClick,
  columnNumber,
  columnId,
  kind,
  value,
  backgroundColor,
  border,
  frozen,
  locked,
  joined,
  indicators = [],
  dragOver,
  setDragOver,
  isAdmin,
  isSynced,
  isLastFrozenColumn,
  viewId,
  draggedColumnId,
  setDraggedColumnId,
  textColor,
  description,
  position = 'absolute'
}) => {
  const themed = useThemedColours()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const {
    spreadsheetData,
    setSpreadsheetData,
    resetSpreadsheetClick,
    selectedColumnIds,
    handleColumnHeaderSelect,
    clearColumnSelection,
    uniqueNumber
  } = useDataContext()
  // The pointer is somewhere in this column (a cell, not just the header).
  const columnHovered = useIsColumnHovered(uniqueNumber, columnId)
  const dragging = useRef<boolean>(false)
  const mouseStart = useRef<number>(0)
  const originalWidth = useRef<number>(width ? width : COLUMN_MIN_WIDTH)
  const widthTimeout = useRef<TimeoutID | null>(null)
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const { project } = useProject()

  const renameInputRef = useRef<HTMLInputElement>(null)
  const cancellingRename = useRef<boolean>(false)

  const rename = useColumnRename({
    savedName: value ?? '',
    save: (name) =>
      new Promise<void>((resolve, reject) => {
        if (columnId === undefined || !setSpreadsheetData) {
          reject(new Error('Column cannot be renamed'))
          return
        }
        updateColumn(
          project.publicId,
          'name',
          spreadsheetData,
          setSpreadsheetData,
          columnId,
          name,
          () => resolve(),
          (error) => reject(error)
        )
      }),
    onSaved: () => {
      setIsEditing(false)
      setSnackbarMessage({ status: 'success', message: 'The column was updated successfully!' })
    }
  })

  // A rejected header rename is prevented rather than left editable: revert to the saved name, close
  // the editor, and report the reason in a toast. (The Edit Column panel keeps an inline error, since
  // it validates as the panel closes.)
  const handleRenameError = (error: unknown) => {
    setIsEditing(false)
    rename.reset(value ?? '')
    displayErrorMessage(error)
  }

  const canRename =
    isAdmin === true && !joined && !isSynced && !!optionClick && columnId !== undefined && kind !== undefined

  const isSelectableColumn = columnId !== undefined && kind !== undefined
  const isColumnSelected = columnId !== undefined && selectedColumnIds.includes(columnId)
  // Its ⋮ shows: the column is hovered or selected (or, through CSS, a tour or a touch screen).
  const optionsShown = isColumnSelected || columnHovered

  // The name comes first: indicators that would cut a word of it short, or run it past three lines,
  // give way, the last first, and the ⋮ takes a dot for them. Measured once laid out, so in tests
  // (no layout) they all show.
  const headerRowRef = useRef<HTMLDivElement>(null)
  const headerEndRef = useRef<HTMLDivElement>(null)
  const [indicatorsShown, setIndicatorsShown] = useState(indicators.length)
  const indicatorKeys = indicators.map((indicator) => indicator.key).join()
  useLayoutEffect(() => {
    const name = headerRowRef.current?.querySelector<HTMLElement>('[data-testid="column-header-name"]')
    const end = headerEndRef.current
    const words =
      name && end && headerRowRef.current?.clientWidth
        ? measureWords(name.textContent ?? '', getComputedStyle(name).font)
        : null
    if (!name || !end || !words) {
      setIndicatorsShown(indicators.length)
      return
    }
    // The name's room with no indicators: up to where they start, plus what they take now.
    const room =
      end.getBoundingClientRect().left -
      name.getBoundingClientRect().left +
      indicatorsWidth(Math.min(indicatorsShown, indicators.length))
    setIndicatorsShown(indicatorsThatFit(indicators.length, room, words.wordWidths, words.spaceWidth))
  }, [width, value, indicatorKeys, indicators.length, indicatorsShown, description, joined, isEditing])
  const shownIndicators = indicators.slice(0, indicatorsShown)

  const hasColumnMenu = !!kind && !!optionClick && !joined
  // The column menu, opened under the ⋮.
  const openColumnMenu = (anchor: HTMLElement) => {
    reconcileSelectionForMenu()
    optionClick?.({
      open: true,
      top: `${anchor.getBoundingClientRect().bottom}px`,
      left: `${anchor.getBoundingClientRect().left - 130}px`,
      right: 'auto',
      bottom: 'auto',
      columnNumber: columnNumber !== undefined ? columnNumber : -1,
      columnId: columnId || '',
      rowNumber: -1,
      rowId: ''
    })
  }
  // An indicator opens the column menu, where what it marks is changed, as the ⋮ beside it does.
  const handleIndicatorClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!hasColumnMenu) return
    event.stopPropagation()
    openColumnMenu(
      headerRowRef.current?.querySelector<HTMLElement>('[data-testid="column-options-button"]') ?? event.currentTarget
    )
  }
  const hiddenIndicators = indicators.slice(indicatorsShown)

  const handleSelectColumn = (event: React.MouseEvent): void => {
    if (!isSelectableColumn || isEditing || columnId === undefined) return
    handleColumnHeaderSelect(columnId, { shiftKey: event.shiftKey, toggleKey: event.metaKey || event.ctrlKey })
  }

  // Before opening a column menu (right-click or the ⋮ button), reconcile the selection so the
  // menu matches it: keep an existing multi-selection when this column is part of it (→ bulk menu),
  // otherwise reset to just this column (→ single-column menu).
  const reconcileSelectionForMenu = (): void => {
    if (columnId === undefined) return
    const isMultiSelection = selectedColumnIds.length >= 2
    if (isMultiSelection && selectedColumnIds.includes(columnId)) return
    handleColumnHeaderSelect(columnId, { shiftKey: false, toggleKey: false })
  }

  useEffect(() => {
    if (isEditing && renameInputRef.current) {
      // prevent swallowing the next genuine commit
      cancellingRename.current = false
      renameInputRef.current.focus()
      renameInputRef.current.select()
    }
  }, [isEditing])

  const startRename = () => {
    if (!canRename) return
    rename.reset(value ?? '')
    setIsEditing(true)
  }

  const cancelRename = () => {
    cancellingRename.current = true
    setIsEditing(false)
    rename.reset(value ?? '')
  }

  const commitRename = () => {
    if (cancellingRename.current) {
      cancellingRename.current = false
      setIsEditing(false)
      return
    }
    // Only a true no-op (empty/unchanged) leaves edit mode immediately. Otherwise the input stays
    // open until the save succeeds (onSaved closes it) or fails (handleRenameError reverts and closes
    // it) — including a still-pending save that settles and picks up this draft.
    if (!rename.commit(rename.draftName, { onError: handleRenameError })) setIsEditing(false)
  }

  const handleRenameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      // Commit via the blur handler so there's a single commit path.
      renameInputRef.current?.blur()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelRename()
    }
  }

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (setDraggedColumnId) {
      const id = event.currentTarget.id
      setDraggedColumnId(id.split('/')[1])
      // The drop-preview ghost renders as soon as a drag starts, so reset the hovered target rather
      // than letting the previous drag's leftover point it somewhere the cursor isn't.
      if (setDragOver) setDragOver('')
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  // TODO: dragging column to the left is not moving the container to the left
  // TODO: prevent vertical scrolling in rows container when dragging and moving a column
  const handleDragEnter = () => {
    if (draggedColumnId && draggedColumnId !== '' && setDragOver && columnId !== undefined) {
      setDragOver(columnId)
    }
  }

  // Fires on the source header however the drag ends, including a cancel or a drop on nothing — the
  // cases where no onDrop runs and the drop-preview ghost would otherwise be left on screen.
  const handleDragEnd = () => {
    if (setDragOver) setDragOver('')
    if (setDraggedColumnId) setDraggedColumnId('')
  }

  // Moves the whole selected block to the drop position in one CHANGE_ALL_COLUMNS_ORDER, mirroring
  // how the Column Order menu commits a group drag. Returns false when there is no block to move, so
  // the caller falls back to the single-column reorder.
  const moveSelectedColumnBlock = (dropColumnId: string): boolean => {
    const block = resolveDraggedColumnBlock(
      draggedColumnId ?? '',
      selectedColumnIds,
      spreadsheetData.userConfiguration.groupSettings.map((setting) => setting.columnId)
    )
    if (block.length < 2 || !setSpreadsheetData) return false
    // Dropping inside the selection is a no-op, but it is still handled here: falling through to the
    // single-column reorder would tear the dragged column out of its own block.
    if (block.includes(dropColumnId)) return true

    const columns = spreadsheetData.viewDetails.columns
    const order = columns.map((column: ITableViewColumn) => column.publicId)
    const newOrder = moveColumnBlock(order, block, dropColumnId)
    if (newOrder === order) return true

    const columnsById = new Map(columns.map((column: ITableViewColumn) => [column.publicId, column]))
    const reordered = newOrder.reduce<ITableViewColumn[]>((acc, id) => {
      const column = columnsById.get(id)
      if (column) acc.push(column)
      return acc
    }, [])

    setSpreadsheetData({ type: 'CHANGE_ALL_COLUMNS_ORDER', columns: reordered })
    return true
  }

  const handleOnDrop = () => {
    if (setDraggedColumnId && setDragOver && columnNumber !== undefined) {
      if (draggedColumnId && draggedColumnId !== '' && setSpreadsheetData) {
        // In transpose views, column reordering is actually row reordering — one column at a time.
        if (hasSyntheticColumns(spreadsheetData.viewDetails.type) && columnId) {
          reorderTransposedColumn(draggedColumnId, columnId, spreadsheetData, setSpreadsheetData, project.publicId)
        } else if (columnId === undefined || !moveSelectedColumnBlock(columnId)) {
          setSpreadsheetData({
            type: 'CHANGE_COLUMN_ORDER',
            columnId: draggedColumnId,
            newIndex: columnNumber
          })
        }
      }
      // Every bulk action ends by dropping the selection (see bulk.tsx's finish()), and dragging a
      // column that isn't part of the selection replaces it outright.
      clearColumnSelection()
      setDragOver('')
      setDraggedColumnId('')
    }
  }

  const handleMouseDown = (event: React.MouseEvent) => {
    dragging.current = true
    originalWidth.current = width ? width : COLUMN_MIN_WIDTH
    mouseStart.current = event.clientX
  }

  const onWidthUpdateSuccess = () => {
    return null
  }

  const onWidthUpdateFailure = () => {
    setSnackbarMessage({ status: 'success', message: 'The column width has been saved successfully' })
  }

  const handleMouseMove = (event: MouseEvent) => {
    if (dragging.current === true) {
      const delta = event.clientX - mouseStart.current
      const newWidth = Math.max(originalWidth.current + delta, COLUMN_MIN_WIDTH)

      if (!setSpreadsheetData || columnId === undefined) return

      if (hasSyntheticColumns(spreadsheetData.viewDetails.type)) {
        if (spreadsheetData.viewDetails.type === ViewTypes.MATRIX) {
          setSpreadsheetData({ type: 'CHANGE_MATRIX_COLUMN_WIDTH', columnId, width: newWidth })
        } else {
          setSpreadsheetData({ type: 'CHANGE_TRANSPOSE_COLUMN_WIDTH', columnId, width: newWidth })
        }
        return
      }

      setSpreadsheetData({ type: 'CHANGE_COLUMN_WIDTH', columnId, columnWidth: newWidth })

      if (isAdmin && viewId !== undefined) {
        if (widthTimeout.current !== null) cancelTimeout(widthTimeout.current)

        widthTimeout.current = requestTimeout(() => {
          updateColumn(
            project.publicId,
            'width',
            spreadsheetData,
            setSpreadsheetData,
            columnId,
            newWidth,
            onWidthUpdateSuccess,
            onWidthUpdateFailure
          )
        }, 2000)
      }
    }
  }

  const handleMouseUp = () => {
    if (dragging.current === false) return

    dragging.current = false
    mouseStart.current = 0
  }

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [columnNumber, width])

  const isSticky = frozen

  return (
    <div
      id={columnId !== undefined ? `column/${columnId}` : `column-number`}
      data-testid={columnId !== undefined ? `table-column` : `table-column-number`}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleOnDrop}
      onDragEnter={handleDragEnter}
      onDragEnd={handleDragEnd}
      onMouseDown={resetSpreadsheetClick}
      draggable={!locked && !isEditing}
      className={headerStyles({
        frozen: isSticky,
        lastFrozen: isLastFrozenColumn,
        dragHighlight: !border && columnId !== undefined && dragOver === columnId,
        noBorder: !!border,
        selected: isColumnSelected,
        class: `${HEADER_HEIGHT_CLASS} ${!isSticky ? position : ''}`
      })}
      style={{
        width: width ? `${width}px` : '100%',
        left: `${cumulativeWidth}px`,
        ...themed.header(backgroundColor, textColor)
      }}
    >
      {/* The name and its icons in proportion with the view tabs above: 13px
          semibold, 14px icons. */}
      <div
        ref={headerRowRef}
        className="w-full h-full flex items-center absolute py-[3px] pl-[8px] pr-[2px] text-[13px] font-semibold [&_svg]:w-3.5 [&_svg]:h-3.5"
        onDoubleClick={startRename}
        onClick={handleSelectColumn}
        onContextMenu={(event: React.MouseEvent) => {
          event.preventDefault()
          if (kind && optionClick && !joined) {
            reconcileSelectionForMenu()
            optionClick({
              open: true,
              top: `${event.clientY + 5}px`,
              left: `${event.clientX - 200}px`,
              right: 'auto',
              bottom: 'auto',
              columnNumber: columnNumber !== undefined ? columnNumber : -1,
              columnId: columnId || '',
              rowNumber: -1,
              rowId: ''
            })
          }
        }}
      >
        {kind && getViewCapabilities(spreadsheetData.viewDetails.type).header.showColumnTypeIcon && (
          // The type is secondary to the name: the header's text colour at 70%,
          // which on the default header is the grey of the view row's icons
          // (#333 over #f5f5f5 blends to about #6d6d6d, against muted's #707070),
          // and on a header with its own colours a lighter shade of them.
          <span className="flex items-center shrink-0 opacity-70" data-testid="column-type-icon">
            <IconSelector kind={kind} />
          </span>
        )}
        {isEditing ? (
          <Input
            ref={renameInputRef}
            type="text"
            value={rename.draftName}
            data-testid="column-rename-input"
            onChange={(event) => rename.setDraftName(event.target.value)}
            onBlur={commitRename}
            onKeyDown={handleRenameKeyDown}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.stopPropagation()}
            className="flex-1 min-w-0 ml-[5px] h-[26px] py-0 px-2 border border-blue font-normal"
          />
        ) : value?.trim() ? (
          // Up to three lines, then cut short with an ellipsis; hovering shows the whole name.
          <Tooltip content={value} showOnlyIfTruncated delay={300}>
            <div className="min-w-0 line-clamp-3 break-words leading-4 ml-[6px]" data-testid="column-header-name">
              {value}
            </div>
          </Tooltip>
        ) : (
          columnId !== undefined && (
            <Tooltip
              className="max-w-[260px] whitespace-normal break-words"
              content="This column has no name. Unnamed columns can't be referenced in formulas or by other columns. Add a name to use it in those places."
            >
              <div className="flex items-center ml-[5px] cursor-help" data-testid="empty-column-name-warning">
                <ErrorOutline className="text-orange" />
              </div>
            </Tooltip>
          )
        )}

        {/* What acts on the column, flush right. The ⋮ beside them keeps its room even while hidden (so the
            name never re-wraps); at rest the icons slide over into it, and move back to show it on hover. */}
        <div ref={headerEndRef} className="flex ml-auto">
          {shownIndicators.length > 0 && (
            // At rest, slid 24px into the ⋮'s room: the last icon then ends 8px inside the header's border,
            // as the type icon starts 8px from the other.
            <div
              className={`flex gap-1 mr-1 transition-transform duration-150 group-hover/header:translate-x-0 [body[data-tour-running]_&]:translate-x-0 [@media(hover:none)]:translate-x-0 ${
                optionsShown || !!description || !(kind && optionClick && !joined)
                  ? 'translate-x-0'
                  : 'translate-x-[24px]'
              }`}
            >
              {shownIndicators.map((indicator) =>
                indicator.element ? (
                  <div key={indicator.key} className="flex items-center justify-center size-3.5 shrink-0">
                    {indicator.element}
                  </div>
                ) : (
                  // In the blue of a toolbar button that's on, so it reads as the same thing; a heavier stroke, as they're
                  // drawn at 14px, and close together, as they're marks rather than buttons.
                  <Tooltip key={indicator.key} content={indicator.label}>
                    <div
                      className={`flex items-center justify-center size-3.5 shrink-0 text-blue [&_svg]:stroke-[2.5] ${hasColumnMenu ? 'cursor-pointer' : ''}`}
                      aria-label={indicator.label}
                      role="img"
                      onClick={handleIndicatorClick}
                      data-testid={`column-indicator-${indicator.key}`}
                    >
                      {indicator.icon}
                    </div>
                  </Tooltip>
                )
              )}
            </div>
          )}
          {joined && (
            <div className="flex items-center justify-center truncate min-w-[26px] max-w-[26px]">
              <Join />
            </div>
          )}
        </div>

        {/* text-inherit so the hint takes the column's own header text colour, not the shared default. */}
        {description && <InfoHint content={getPlainTextDescription(description)} className="text-inherit" />}
        {kind && optionClick && !joined && (
          // Names what gave way to the name, when anything did.
          <Tooltip
            content={hiddenIndicators.map((indicator) => indicator.label).join(' · ')}
            visible={hiddenIndicators.length > 0 ? undefined : false}
          >
            <div
              data-tour="column-options-button"
              // Only on the column you're in (its header or any of its cells), or
              // have selected, or that a product tour is pointing at, so the header
              // row stays calm; always on touch screens, which have no hover. It
              // keeps its space when hidden, so names don't shift as you move across.
              data-testid="column-options-button"
              aria-label={`${value?.trim() ? `${value} options` : 'Column options'}${hiddenIndicators.length > 0 ? `: ${hiddenIndicators.map((indicator) => indicator.label).join(', ')}` : ''}`}
              aria-haspopup="menu"
              className={`relative flex items-center justify-center cursor-pointer min-w-[26px] max-w-[26px] transition-opacity group-hover/header:opacity-100 focus-visible:opacity-100 [body[data-tour-running]_&]:opacity-100 [@media(hover:none)]:opacity-100 ${isColumnSelected || columnHovered ? 'opacity-100' : 'opacity-0'}`}
              // A tab stop with Enter and Space, so the menu opens from the keyboard too.
              {...rowButtonProps((event) => {
                // Don't let this bubble to the header's onClick (handleSelectColumn), which would
                // reset a multi-selection to just this column before the menu opens, or a key
                // press reach the grid's own key handling.
                event.stopPropagation()
                openColumnMenu(event.currentTarget)
              })}
            >
              <Dots />
              {hiddenIndicators.length > 0 && (
                <span
                  aria-hidden
                  className="absolute -top-0.5 right-1 size-1.5 rounded-full bg-blue"
                  data-testid="column-options-dot"
                />
              )}
            </div>
          </Tooltip>
        )}
        {columnNumber !== undefined && getViewCapabilities(spreadsheetData.viewDetails.type).header.resizeColumn && (
          <div
            className="absolute cursor-ew-resize w-4 z-[2] top-[-1px] right-[-7.5px]"
            style={{ height: `${HEADER_HEIGHT - 2}px` }}
            onMouseDown={(event: React.MouseEvent) => {
              event.preventDefault()
              handleMouseDown(event)
            }}
          >
            <div className="absolute block rounded h-full bg-blue opacity-0 hover:opacity-100 left-[7px] w-[3px]" />
          </div>
        )}
        {/* Translucent, so the column's name shows through the tint: an opaque
            fill here sits over the header content and hides it. */}
        {isColumnSelected && <div className="absolute inset-0 pointer-events-none z-[1] bg-blue/15" />}
      </div>
    </div>
  )
}

export default Header
